import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowsClockwise, CheckCircle, Lock, Stack } from "@phosphor-icons/react";
import { DashboardLayout } from "../components/DashboardLayout";
import { PageHeader, Spinner, CategoryBadge } from "../components/ui-bits";
import { IssuePassportModal } from "../components/IssuePassportModal";
import { api, getMerchantId, CATEGORY_LABELS, CATEGORY_PRICE, CATEGORY_PENCE } from "../lib/api";

export default function Products() {
  const merchantId = getMerchantId();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [selected, setSelected] = useState(null);
  const [selectMode, setSelectMode] = useState(false);
  const [chosen, setChosen] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/products?merchant_id=${merchantId}`);
      setProducts(data.products);
    } catch {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/products/sync", { merchant_id: merchantId });
      toast.success(`${data.synced} products synced`);
      load();
    } catch {
      toast.error("Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const toggleSelect = (id) => setChosen((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));
  const chosenProducts = products.filter((p) => chosen.includes(p.id));
  const totalPence = chosenProducts.reduce((s, p) => s + (CATEGORY_PENCE[p.category] || 299), 0);
  const exitSelect = () => { setSelectMode(false); setChosen([]); };
  const bulkIssue = async () => {
    if (chosen.length === 0) return;
    setBulkLoading(true);
    try {
      const { data } = await api.post("/dpp/create-bulk-payment-intent", { product_ids: chosen, merchant_id: merchantId, origin_url: window.location.origin });
      localStorage.setItem("vcoin_pending_bulk", JSON.stringify({ session_id: data.session_id, merchant_id: merchantId }));
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start bulk checkout");
      setBulkLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <PageHeader
        overline="Shopline Catalogue"
        title="Products"
        subtitle="Select an item to issue its permanent Digital Product Passport."
        action={
          <div className="flex gap-3 self-start">
            <button data-testid="bulk-toggle-button" onClick={() => (selectMode ? exitSelect() : setSelectMode(true))} className={`flex items-center gap-2 rounded-full px-5 py-3 text-sm transition-colors ${selectMode ? "bg-gold text-navy" : "bg-white border border-navy/15 text-navy hover:border-gold hover:text-gold"}`}>
              <Stack size={17} weight="light" /> {selectMode ? "Cancel" : "Bulk Issue"}
            </button>
            <button data-testid="sync-products-button" onClick={sync} disabled={syncing} className="flex items-center gap-2 bg-navy text-cream rounded-full px-5 py-3 text-sm hover:brightness-110 active:scale-[0.99] transition-all disabled:opacity-60">
              {syncing ? <Spinner className="h-4 w-4" /> : <ArrowsClockwise size={17} weight="light" />} Sync
            </button>
          </div>
        }
      />

      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {[...Array(8)].map((_, i) => <div key={i} className="h-72 rounded-2xl bg-white/60 border border-navy/5 animate-pulse" />)}
        </div>
      ) : products.length === 0 ? (
        <div className="bg-white rounded-2xl border border-navy/10 p-12 text-center text-navy/50">
          No products found. Click Sync Products to pull your Shopline catalogue.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {products.map((p) => (
            <button
              key={p.id}
              data-testid="product-card"
              onClick={() => {
                if (p.tokenized) return;
                if (selectMode) toggleSelect(p.id); else setSelected(p);
              }}
              className={`text-left bg-white rounded-2xl border shadow-luxe overflow-hidden transition-all duration-300 ${p.tokenized ? "opacity-90 cursor-default border-navy/10" : "hover:-translate-y-1 hover:shadow-luxe-lg cursor-pointer"} ${selectMode && chosen.includes(p.id) ? "border-gold ring-2 ring-gold" : "border-navy/10"}`}
            >
              <div className="h-44 bg-cream overflow-hidden relative">
                {p.image_url ? <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" /> : null}
                {selectMode && !p.tokenized && (
                  <span className="absolute top-3 left-3 h-6 w-6 rounded-full flex items-center justify-center" style={{ background: chosen.includes(p.id) ? "#C9A84C" : "rgba(255,255,255,0.9)", border: "1px solid rgba(27,43,94,0.2)" }}>
                    {chosen.includes(p.id) ? <CheckCircle size={16} weight="fill" className="text-navy" /> : null}
                  </span>
                )}
                {p.tokenized ? (
                  <span className="absolute top-3 right-3 flex items-center gap-1 bg-score-green text-white text-[0.6rem] uppercase tracking-wide rounded-full px-2.5 py-1">
                    <CheckCircle size={12} weight="fill" /> Passport Issued
                  </span>
                ) : (
                  <span className="absolute top-3 right-3 flex items-center gap-1 bg-navy/80 text-cream text-[0.6rem] uppercase tracking-wide rounded-full px-2.5 py-1">
                    <Lock size={11} weight="light" /> Untokenized
                  </span>
                )}
              </div>
              <div className="p-5">
                <CategoryBadge label={CATEGORY_LABELS[p.category] || p.category} />
                <p className="mt-2 text-navy font-medium leading-snug">{p.name}</p>
                <p className="text-xs text-navy/45 mt-0.5">{p.brand}</p>
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-navy font-playfair text-lg">£{Number(p.price).toLocaleString()}</span>
                  {!p.tokenized && <span className="text-[0.68rem] text-gold">Fee {CATEGORY_PRICE[p.category]}</span>}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {selectMode && chosen.length > 0 && (
        <div className="fixed bottom-0 inset-x-0 lg:left-64 z-40 bg-navy text-cream px-6 py-4 flex items-center justify-between shadow-luxe-lg" data-testid="bulk-bar">
          <div>
            <p className="text-sm">{chosen.length} product{chosen.length > 1 ? "s" : ""} selected</p>
            <p className="text-xs text-cream/60">Combined fee £{(totalPence / 100).toFixed(2)}</p>
          </div>
          <button data-testid="bulk-issue-button" onClick={bulkIssue} disabled={bulkLoading} className="flex items-center gap-2 bg-gold text-navy font-medium rounded-full px-6 py-3 text-sm hover:brightness-105 active:scale-[0.99] transition-all disabled:opacity-60">
            {bulkLoading ? <Spinner className="h-4 w-4" color="#1B2B5E" /> : <CheckCircle size={17} weight="fill" />} Issue {chosen.length} · £{(totalPence / 100).toFixed(2)}
          </button>
        </div>
      )}

      {selected && (
        <IssuePassportModal
          product={selected}
          merchantId={merchantId}
          onClose={() => setSelected(null)}
          onIssued={() => { setSelected(null); load(); }}
        />
      )}
    </DashboardLayout>
  );
}
